// Wie lange ein Tab braucht, bis er samt Inhalt dasteht.
//
// Die Zahl in der Statusleiste war bisher die des ERSTEN Seitenaufrufs
// (Navigation-Timing) und aenderte sich danach nie wieder. In der Shell
// ist der Seitenaufruf aber nur der Anfang: jeder Tab-Wechsel baut den
// Inhalt komplett neu auf -- `{#key tab.id}` in EditorArea wirft den
// alten weg und montiert den neuen. Genau diese Zeit ist die, die man
// beim Arbeiten spuert.
//
// Gemessen wird in zwei Abschnitten:
//
//   Aufbau  t0 -> das erste Bild des neuen Tabs steht.
//           Enthaelt das Abraeumen des vorigen Tabs (bei grossen
//           Tabellen der teuerste Teil, siehe docs/data-loading.md
//           "Abraeumen kostet"), das Aufloesen des Chunks und das
//           Montieren der Seite.
//   Daten   das erste Bild -> die Abrufe, die der Tab ausgeloest hat,
//           sind durch. Bei einem SWR-Treffer ist das null: der Tab
//           stand sofort komplett da.
//
// t0 ist der Moment, in dem der andere Tab aktiv wird -- gesetzt aus
// einem `$effect.pre`, also VOR dem Neuaufbau. Ein spaeterer Startpunkt
// wuerde genau den Teil verschweigen, den man als Ruckeln wahrnimmt.
//
// Genauigkeitsgrenze, ehrlich benannt: "Daten" zaehlt alle
// /v2-api/-Abrufe, die nach t0 beginnen. Ein Hintergrund-Poll, der
// zufaellig in dieses Fenster faellt, wird mitgezaehlt und schiebt die
// Zahl um seine Dauer nach oben. Die Abrufe einem Tab sauber
// zuzuordnen ginge nur, wenn jeder Store seinen Anlass mitfuehrte --
// das waere ein Umbau quer durch alle Stores fuer eine Anzeige.

/** Ruhefenster: so lange muss es still sein, damit ein Tab als fertig
 *  gilt. Kurz genug, um nicht zu bummeln; lang genug, dass eine
 *  Nachlade-Kette (Store A loest Store B aus) nicht auseinanderfaellt. */
const RUHE_MS = 250;

/** Notbremse. Wer bis dahin nicht zur Ruhe kommt (dauernd nachladende
 *  Ansicht), bekommt die Zahl mit Tilde -- lieber ein ehrliches
 *  "mindestens" als gar nichts. */
const DECKEL_MS = 15_000;

export interface TabLadezeit {
  titel: string;
  /** t0 -> fertig, in ms. */
  gesamt: number;
  /** Anteil bis zum ersten Bild. */
  aufbau: number;
  /** Anteil danach, bis die Abrufe durch waren. */
  daten: number;
  /** true = der Deckel hat gegriffen, die Zahl ist eine Untergrenze. */
  gedeckelt: boolean;
}

// Laufender Vorgang. Bewusst PLAIN, nicht $state: diese Werte werden aus
// einem $effect.pre heraus geschrieben. Waeren sie reaktiv, laese der
// Effekt was er schreibt und weckte sich selbst -- die Falle, die am
// 23.08.2026 306 Abrufe in 14 Sekunden erzeugt hat.
let _tabId: string | null = null;
let _titel = "";
let _t0 = 0;
let _aufbau = 0; // performance.now() beim ersten Bild, 0 = noch nicht da
let _letzteAktivitaet = 0;
let _laeuft = false;
let _ruheTimer: ReturnType<typeof setTimeout> | undefined;
let _deckelTimer: ReturnType<typeof setTimeout> | undefined;

// NUR das Ergebnis ist reaktiv -- das liest die Statusleiste.
let _ergebnis = $state<TabLadezeit | null>(null);

let _beobachter: PerformanceObserver | undefined;

/** Zaehlt dieser Eintrag als "Inhalt des Tabs"? */
function zaehlt(e: PerformanceEntry): boolean {
  const n = e.name;
  if (!n.includes("/v2-api/")) return false;
  // Dauerverbindungen enden nie und wuerden jede Messung deckeln.
  if (n.includes("stream")) return false;
  return true;
}

function beobachterStarten(): void {
  if (_beobachter || typeof PerformanceObserver === "undefined") return;
  try {
    _beobachter = new PerformanceObserver((liste) => {
      if (!_laeuft) return;
      let neu = false;
      for (const e of liste.getEntries()) {
        if (!zaehlt(e)) continue;
        // Nur was NACH dem Tab-Wechsel begann, gehoert zu diesem Tab.
        if (e.startTime < _t0) continue;
        const ende = e.startTime + e.duration;
        if (ende > _letzteAktivitaet) _letzteAktivitaet = ende;
        neu = true;
      }
      if (neu) planeRuhe();
    });
    _beobachter.observe({ type: "resource", buffered: false });
  } catch {
    // Kein Resource-Timing: dann gibt es eben nur den Aufbau-Anteil.
  }
}

function planeRuhe(): void {
  if (!_laeuft || !_aufbau) return; // vor dem ersten Bild ist nichts fertig
  if (_ruheTimer) clearTimeout(_ruheTimer);
  _ruheTimer = setTimeout(() => abschluss(false), RUHE_MS);
}

function abschluss(gedeckelt: boolean): void {
  if (!_laeuft || !_aufbau) return;
  _laeuft = false;
  if (_ruheTimer) clearTimeout(_ruheTimer);
  if (_deckelTimer) clearTimeout(_deckelTimer);
  _ruheTimer = undefined;
  _deckelTimer = undefined;

  // Das Ruhefenster selbst ist Messwerkzeug, keine Ladezeit -- gezaehlt
  // wird bis zur letzten echten Regung, nicht bis zum Ablauf des Timers.
  const ende = Math.max(_aufbau, _letzteAktivitaet);
  _ergebnis = {
    titel: _titel,
    gesamt: Math.max(0, Math.round(ende - _t0)),
    aufbau: Math.max(0, Math.round(_aufbau - _t0)),
    daten: Math.max(0, Math.round(ende - _aufbau)),
    gedeckelt,
  };
}

/**
 * Neuen Tab-Aufbau messen. Aus `$effect.pre` aufrufen, damit der
 * Startpunkt vor dem Neuaufbau liegt.
 *
 * Mehrfachaufrufe mit derselben ID sind ein No-op: ein `$effect` haengt
 * an mehr Werten als nur der Tab-ID (die Tab-Liste selbst zaehlt dazu),
 * und jeder davon wuerde sonst die laufende Messung zuruecksetzen.
 */
export function starteTabMessung(tabId: string, titel: string): void {
  if (tabId === _tabId) return;
  beobachterStarten();
  if (_ruheTimer) clearTimeout(_ruheTimer);
  if (_deckelTimer) clearTimeout(_deckelTimer);
  _tabId = tabId;
  _titel = titel;
  _t0 = performance.now();
  _aufbau = 0;
  _letzteAktivitaet = 0;
  _laeuft = true;
  _ruheTimer = undefined;
  _deckelTimer = setTimeout(() => {
    // Kein erstes Bild bis hierher: die Messung stillschweigend fallen
    // lassen, statt eine Fantasiezahl zu zeigen.
    if (_aufbau) abschluss(true);
    else _laeuft = false;
  }, DECKEL_MS);
}

/**
 * Das erste Bild des Tabs steht. Ruft der Wirt, sobald der Inhalt
 * wirklich gerendert ist -- fuer Seiten-Tabs also erst, wenn die Seite
 * hinter dem Lazy-Chunk gemountet ist, nicht schon der Wirt selbst.
 */
export function meldeTabAufgebaut(): void {
  if (!_laeuft || _aufbau) return;
  // Zwei Frames: nach dem ersten hat Svelte das DOM geschrieben, nach
  // dem zweiten hat der Browser es auch gemalt.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!_laeuft || _aufbau) return;
      _aufbau = performance.now();
      if (_letzteAktivitaet < _aufbau) {
        // Bis hierher kam nichts aus dem Netz -- dann ist der Tab mit
        // dem ersten Bild fertig, sofern gleich nichts mehr nachkommt.
        _letzteAktivitaet = 0;
      }
      planeRuhe();
    });
  });
}

export function getTabLadezeit(): TabLadezeit | null {
  return _ergebnis;
}
